import React, { createContext, useMemo, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../../authentication';
import { useUpdateAnswerMutation } from '../hooks/useUpdateAnswerMutation';
import { useOrganizations } from '../../organization/providers';
import { useQuestions } from './QuestionsProvider';

const AnswersContext = createContext();

const AnswersProvider = ({ children = null }) => {
  const { accessToken } = useAuth();
  const { currentOrganization } = useOrganizations();
  const { updateQuestionAnswer } = useQuestions();
  const queryClient = useQueryClient();

  const updateAnswerMutation = useUpdateAnswerMutation(accessToken, currentOrganization?.id);

  const updateAnswer = useCallback(
    async (answerId, data) => {
      // Optimistically update the answer in the cache
      const assetsQueryKey = ['chat', 'questions', 'assets', currentOrganization?.id];
      const previousState = queryClient.getQueryData(assetsQueryKey);

      try {
        // Optimistically update the cache
        if (previousState) {
          const updateAnswerRecursively = (questions) =>
            questions?.map((q) => {
              // Update answer if found in current question
              if (q.answers?.some((a) => a.id === answerId)) {
                return {
                  ...q,
                  answers: q.answers.map((a) => (a.id === answerId ? { ...a, ...data } : a))
                };
              }

              // Recursively check followUps if they exist
              if (q.followUps) {
                return {
                  ...q,
                  followUps: updateAnswerRecursively(q.followUps)
                };
              }

              return q;
            });

          queryClient.setQueryData(assetsQueryKey, (oldData) => updateAnswerRecursively(oldData));
        }

        // Perform the actual mutation
        const updatedAnswer = await updateAnswerMutation.mutateAsync({ answerId, data });
        updateQuestionAnswer(updatedAnswer.data);

        return updatedAnswer.data;
      } catch (error) {
        // Revert the optimistic update if the mutation fails
        if (previousState) {
          queryClient.setQueryData(assetsQueryKey, previousState);
        }
        throw error;
      }
    },
    [updateAnswerMutation, queryClient, currentOrganization, updateQuestionAnswer]
  );

  const value = useMemo(() => ({ updateAnswer }), [updateAnswer]);

  return <AnswersContext.Provider value={value}>{children}</AnswersContext.Provider>;
};

AnswersProvider.propTypes = {
  children: PropTypes.node
};

const useAnswers = () => useContext(AnswersContext);

export { AnswersProvider, AnswersContext, useAnswers };
